import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Barcode } from "lucide-react";
import { customFetch } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractApiErrorMessage } from "@/lib/api-error";
import { formatCurrency } from "@/lib/utils";
import { ehCodigoDeBarras, limparCodigoProduto, codigoProdutoValido } from "@workspace/text";

// Campo "Código do produto" do cadastro (10/09/2026; código próprio em
// 22/09/2026). Aceita código de barras (só dígitos, 8–14) OU um código que a
// loja inventa ("CESTA-01") — a regra é a de @workspace/text (productCode),
// a mesma do servidor e do bipe do PDV. Só o código de barras consulta. O leitor
// USB (Elgin EL250) é um teclado: digita os dígitos e manda Enter. O Enter é
// capturado AQUI com preventDefault — o modal é um <form>, e sem isso a
// leitura viraria "Salvar Produto" com o produto pela metade.
//
// O campo consulta /api/barcode/lookup e entrega o resultado ao pai
// (onResult), que aplica as regras de preenchimento. Aqui ficam só: status
// em uma linha, aviso de duplicidade com atalho, miniaturas das fotos e o
// preço de referência.

export interface LookupResult {
  gtin: string;
  gtinCanonico: string;
  tipo: string | null;
  valido: boolean;
  consultavel: boolean;
  motivo: string | null;
  duplicado: { id: string; name: string } | null;
  outrasLojas: { lojas: number; nome: string | null; categoria: string | null } | null;
  encontrado: boolean;
  sugestao: {
    nome: string | null;
    marca: string | null;
    descricao: string | null;
    categorias: string[];
    imagens: Array<{ url: string; fonte: string }>;
    precoReferencia: { min: number; max: number; medio: number; lojas: number | null; fonte: string } | null;
    quantidade: string | null;
  } | null;
  cache: "hit" | "miss" | null;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  /** Resultado da consulta; o pai preenche só o que está vazio. */
  onResult: (r: LookupResult) => void;
  /** Lojista clicou em "editar" no aviso de duplicidade. */
  onEditDuplicate: (productId: string) => void;
  /** Quantas fotos ainda cabem no produto. */
  imageSlots: number;
  /** Importa a foto (servidor baixa e reenvia pro CDN) e entra na lista do produto. */
  onImportImage: (url: string) => Promise<void>;
  disabled?: boolean;
  /** Produto sendo editado — não conta como duplicado de si mesmo. */
  excludeId?: string | null;
}

export function BarcodeLookupField({ value, onChange, autoFocus, onResult, onEditDuplicate, imageSlots, onImportImage, disabled, excludeId }: Props) {
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [erro, setErro] = useState<string | null>(null);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [importadas, setImportadas] = useState<string[]>([]);
  const [importando, setImportando] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const limpo = limparCodigoProduto(value);
  const ehBarras = ehCodigoDeBarras(limpo);
  const proprioInvalido = !!limpo && !ehBarras && !codigoProdutoValido(limpo);
  // Duplicidade do código PRÓPRIO: checada ao vivo (500 ms depois de parar de
  // digitar) em /api/products/codigo/:codigo. Pro GTIN o aviso vem do lookup.
  const [duplicadoProprio, setDuplicadoProprio] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    if (!limpo || ehBarras || proprioInvalido) { setDuplicadoProprio(null); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const q = excludeId ? `?exceto=${encodeURIComponent(excludeId)}` : "";
        const r = await customFetch<{ duplicado: { id: string; name: string } | null }>(`/api/products/codigo/${encodeURIComponent(limpo)}${q}`);
        if (vivo) setDuplicadoProprio(r.duplicado);
      } catch { if (vivo) setDuplicadoProprio(null); }
    }, 500);
    return () => { vivo = false; clearTimeout(t); };
  }, [limpo, ehBarras, proprioInvalido, excludeId]);
  const duplicado = result?.duplicado ?? duplicadoProprio;

  const consultar = async () => {
    if (!ehBarras || status === "loading") return;
    const seq = ++seqRef.current;
    setStatus("loading"); setErro(null); setResult(null); setImportadas([]);
    try {
      const r = await customFetch<LookupResult>(`/api/barcode/lookup/${limpo}`);
      if (seq !== seqRef.current) return;
      setResult(r); setStatus("done");
      onChange(r.gtin);
      onResult(r);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setErro(extractApiErrorMessage(e, "Não consegui consultar agora."));
      setStatus("error");
    }
  };

  const importar = async (url: string) => {
    if (importando || importadas.includes(url) || imageSlots <= 0) return;
    setImportando(url);
    try {
      await onImportImage(url);
      setImportadas((prev) => [...prev, url]);
    } catch (e) {
      setErro(extractApiErrorMessage(e, "Não consegui importar a foto."));
    } finally {
      setImportando(null);
    }
  };

  const linhaStatus = (() => {
    if (status === "loading") return { texto: "Consultando…", cls: "text-muted-foreground" };
    if (status === "error" && erro) return { texto: erro, cls: "text-destructive" };
    if (proprioInvalido) return { texto: "Use letras, números e hífen (2 a 30).", cls: "text-destructive" };
    if (limpo && !ehBarras) return duplicadoProprio ? null : { texto: "Código próprio da loja, sem consulta.", cls: "text-muted-foreground" };
    if (!result) return null;
    if (result.duplicado) return null; // renderizado à parte, com o atalho
    if (!result.consultavel) return { texto: result.motivo || "Sem consulta para este código.", cls: "text-muted-foreground" };
    if (result.encontrado) {
      const outras = result.outrasLojas && result.outrasLojas.lojas > 0 && result.outrasLojas.nome
        ? ` · ${result.outrasLojas.lojas} ${result.outrasLojas.lojas === 1 ? "loja cadastrou" : "lojas cadastraram"} como "${result.outrasLojas.nome}"`
        : "";
      return { texto: `Encontrado${outras}`, cls: "text-green-700 dark:text-green-400" };
    }
    if (result.outrasLojas && result.outrasLojas.lojas > 0 && result.outrasLojas.nome) {
      return { texto: `${result.outrasLojas.lojas} ${result.outrasLojas.lojas === 1 ? "loja cadastrou" : "lojas cadastraram"} como "${result.outrasLojas.nome}"`, cls: "text-green-700 dark:text-green-400" };
    }
    return { texto: "Não encontrado — preencha o nome.", cls: "text-muted-foreground" };
  })();

  const ref = result?.sugestao?.precoReferencia ?? null;
  const fotos = result?.sugestao?.imagens ?? [];

  return (
    <div className="space-y-2" data-testid="barcode-field">
      <Label>Código do produto</Label>
      <div className="flex gap-2">
        <div className="relative flex-1 min-w-0">
          <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={value}
            autoComplete="off"
            autoCapitalize="characters"
            placeholder="Leia o código de barras ou crie o seu"
            className="pl-9 font-mono"
            disabled={disabled}
            onChange={(e) => { onChange(limparCodigoProduto(e.target.value)); if (status !== "idle") { setStatus("idle"); setResult(null); setErro(null); } }}
            onKeyDown={(e) => {
              // Enter do leitor: consulta (se for código de barras), nunca submete o formulário.
              if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); void consultar(); }
            }}
            data-testid="barcode-input"
          />
        </div>
        <Button type="button" variant="outline" className="shrink-0" onClick={() => void consultar()} disabled={disabled || !ehBarras || status === "loading"} data-testid="barcode-lookup">
          {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          <span className="ml-1.5 hidden sm:inline">Buscar dados</span>
        </Button>
      </div>
      {duplicado && (
        <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="barcode-duplicate">
          Já cadastrado como "{duplicado.name}".{" "}
          <button type="button" className="underline font-semibold" onClick={() => onEditDuplicate(duplicado.id)}>editar</button>
        </p>
      )}
      {linhaStatus && <p className={`text-xs ${linhaStatus.cls}`} data-testid="barcode-status">{linhaStatus.texto}</p>}
      {ref && (
        <p className="text-xs text-muted-foreground" data-testid="barcode-price-ref">
          Referência: {ref.min === ref.max ? formatCurrency(ref.min) : `${formatCurrency(ref.min)} a ${formatCurrency(ref.max)}`}
          {ref.lojas ? ` em ${ref.lojas} ${ref.lojas === 1 ? "loja online" : "lojas online"}` : ` (${ref.fonte})`}
        </p>
      )}
      {fotos.length > 0 && (
        <div className="flex items-center gap-2" data-testid="barcode-photos">
          {fotos.map((f) => {
            const feita = importadas.includes(f.url);
            const ocupado = importando === f.url;
            return (
              <button
                key={f.url}
                type="button"
                title={feita ? "Foto adicionada" : `Adicionar foto (${f.fonte})`}
                disabled={feita || !!importando || imageSlots <= 0}
                onClick={() => void importar(f.url)}
                className={`relative w-14 h-14 rounded-lg overflow-hidden border-2 ${feita ? "border-green-500" : "border-border hover:border-primary"} disabled:opacity-60`}
              >
                <img src={f.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                {ocupado && <span className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="w-4 h-4 text-white animate-spin" /></span>}
                {feita && <span className="absolute bottom-0 inset-x-0 bg-green-600 text-white text-[10px] text-center">ok</span>}
              </button>
            );
          })}
          <span className="text-xs text-muted-foreground">{imageSlots <= 0 ? "Sem espaço para mais fotos" : "Toque para adicionar"}</span>
        </div>
      )}
    </div>
  );
}
